/**
 * Finds names from a guide (beef cuts, ingredients) in a line of menu text.
 * Ported from the testkitchen menu-cuts prototype and made generic.
 *
 * Both sides are folded: NFKC (half-width ｶﾙﾋﾞ becomes カルビ), katakana to
 * hiragana, lower case, separators dropped, so ザブトン, ざぶとん and ｻﾞﾌﾞﾄﾝ are
 * one name. The longest name wins. Latin names match as whole words.
 */

const SEP = /[\s\-‐・·'’"“”.,()（）&＆/／|｜:：…~〜～☆★◆◇●○◎※「」『』【】[\]]/;
const kataToHira = (s: string) => s.replace(/[ァ-ヶ]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0x60));
export const fold = (s: string) => kataToHira(String(s).normalize('NFKC').toLowerCase()).split('').filter((c) => !SEP.test(c)).join('');
const latinFold = (s: string) => String(s).normalize('NFKD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z]+/g, ' ').trim();
const scriptOf = (ch?: string) => (!ch ? '' : /[ァ-ヺー]/.test(ch) ? 'kata' : /[ぁ-ゖ]/.test(ch) ? 'hira' : '');

export interface GuideEntry {
  /** A name as printed: kanji, kana, romaji or English. Parentheses and 、 split it into several names. */
  raw: string;
  key: string;
  /** Lower wins when one name points at several things */
  rank: number;
}

export interface GuideHit {
  /** Character offsets into the line's NFKC text */
  start: number;
  end: number;
  term: string;
  keys: string[];
}

export interface MatcherOptions {
  entries: GuideEntry[];
  /** Folded names never matched */
  exclude?: string[];
  /** Folded words that contain a name but aren't one; matches inside them are dropped */
  stop?: string[];
  /** Kana names up to this many characters must not sit inside a longer word of the same script. 2 = both sides checked, 3-4 = before only */
  guardMax?: number;
  okAfter?: string[];
  okBefore?: string[];
  /** Lower-case Latin names never matched */
  latinExclude?: string[];
  /** Single characters (柿, 鯛) count only when they are the whole line */
  singleWhole?: boolean;
  minLatin?: number;
}

export function createMatcher(opts: MatcherOptions) {
  const exclude = new Set((opts.exclude || []).map(fold));
  const stop = (opts.stop || []).map(fold);
  const okAfter = (opts.okAfter || []).map(fold);
  const okBefore = (opts.okBefore || []).map(fold);
  const latinExclude = new Set(opts.latinExclude || []);
  const guardMax = opts.guardMax ?? 4;
  const minLatin = opts.minLatin ?? 4;

  const terms = new Map<string, { key: string; rank: number }[]>();
  const latin = new Map<string, { key: string; rank: number }[]>();
  const push = (m: typeof terms, t: string, key: string, rank: number) => {
    const list = m.get(t);
    if (list) list.push({ key, rank });
    else m.set(t, [{ key, rank }]);
  };
  for (const { raw, key, rank } of opts.entries) {
    for (const part of String(raw).split(/[（）()、,，]/)) {
      const t = part.trim();
      if (!t) continue;
      // eslint-disable-next-line no-control-regex
      if (/^[\x00-\x7F]+$/.test(t)) {
        const l = latinFold(t);
        if (l.length >= minLatin && !latinExclude.has(l)) push(latin, l, key, rank);
        continue;
      }
      const f = fold(t);
      const len = [...f].length;
      if (len < (opts.singleWhole ? 1 : 2) || exclude.has(f)) continue;
      push(terms, f, key, rank);
    }
  }
  const termList = [...terms.keys()].sort((a, b) => b.length - a.length);
  const latinList = [...latin.keys()].sort((a, b) => b.length - a.length);
  const latinRes = new Map(latinList.map((t) => [t, new RegExp(`(?<![a-z])${t.replace(/ /g, '[ -]?')}(?![a-z])`, 'g')]));
  const best = (cands: { key: string; rank: number }[]) => {
    const top = Math.min(...cands.map((c) => c.rank));
    return [...new Set(cands.filter((c) => c.rank === top).map((c) => c.key))];
  };

  return function find(text: string): GuideHit[] {
    const chars = [...String(text).normalize('NFKC')];
    let out = '';
    const at: number[] = [];
    chars.forEach((c, i) => {
      // OCR sometimes reads the long mark ー as a dash: ロ-ス
      let ch = c;
      if (/[-‐―—]/.test(ch) && /[ァ-ヺぁ-ゖー]/.test(chars[i - 1] || '')) ch = 'ー';
      for (const f of kataToHira(ch.toLowerCase())) {
        if (!SEP.test(f)) { out += f; at.push(i); }
      }
    });
    const folded = out;
    const blocked = new Uint8Array(out.length);
    const taken = new Uint8Array(out.length);
    for (const w of stop) for (let i = out.indexOf(w); i >= 0; i = out.indexOf(w, i + 1)) blocked.fill(1, i, i + w.length);
    const hits: GuideHit[] = [];
    for (const term of termList) {
      const tlen = [...term].length;
      if (tlen === 1 && out !== term) continue;
      for (let i = out.indexOf(term); i >= 0; i = out.indexOf(term, i + 1)) {
        const j = i + term.length;
        if (taken.subarray(i, j).some(Boolean) || blocked.subarray(i, j).some(Boolean)) continue;
        if (tlen <= guardMax && /^[ぁ-ゖー]+$/.test(term)) {
          const first = chars[at[i]], last = chars[at[j - 1]];
          const prev = chars[at[i] - 1], next = chars[at[j - 1] + 1];
          const badPrev = prev && scriptOf(prev) && scriptOf(prev) === scriptOf(first) && !okBefore.some((w) => folded.slice(0, i).endsWith(w));
          const badNext = tlen <= 2 && next && scriptOf(next) && scriptOf(next) === scriptOf(last) && !okAfter.some((w) => folded.slice(j).startsWith(w));
          if (badPrev || badNext) continue;
        }
        taken.fill(1, i, j);
        hits.push({ start: at[i], end: at[j - 1] + 1, term, keys: best(terms.get(term)!) });
      }
    }
    // Whole-word Latin names, on a copy with accents dropped
    const lat = chars.map((ch) => { const f = latinFold(ch); return f ? f[0] : /[\s\-‐]/.test(ch) ? ' ' : '\u0000'; }).join('');
    const used = new Uint8Array(chars.length);
    for (const h of hits) used.fill(1, h.start, h.end);
    for (const term of latinList) {
      for (const m of lat.matchAll(latinRes.get(term)!)) {
        const i = m.index!, j = i + m[0].length;
        if (used.subarray(i, j).some(Boolean)) continue;
        used.fill(1, i, j);
        hits.push({ start: i, end: j, term, keys: best(latin.get(term)!) });
      }
    }
    return hits.sort((a, b) => a.start - b.start);
  };
}
